import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { GeneralPlayer, PublishedIcon, toast, Tooltip } from "..";
import { DRIVE_TAB } from "@/utils/constants/tabConstants";
import { formatEntityName } from "@/utils/utilFunctions/entityName";
import {
  copyTextToClipboard,
  fileProcessingStatusToTextAndColor,
  formatTimestampToAgo,
  getDenmarkMediaUrlWithSeopath,
  getHostLogoStyle,
  getHostLogoStyleByHostId,
  openVideoInNewTab,
} from "../../utils";
import { Button } from "..";
import CustomUpload from "../../assets/icons/svg/customUpload.svg?react";
import { socialPublishStatusToTextAndColor } from "../../utils";
import SandboxIcon from "../../assets/icons/svg/hourglass.svg?react";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import { RootState } from "../../store";
import {
  FACEBOOK_URL,
  INSTAGRAM_URL,
  LINKEDIN_URL,
  TWITTER_URL,
  YOUTUBE_URL,
} from "../../utils";
import DenmarkPublishPopover from "../MediaPublish/DenmarkPublish/DenmarkPublishPopover";
import DenmarkEditModal from "../MediaPublish/DenmarkPublish/DenmarkEditModal";
import {
  callDenmarkDetailApi,
  callDenmarkArticleGetApi,
} from "@/store/upload/upload.action";
import { DenmarkPublish as DenmarkPublishInterface } from "@/database";

interface GeneralPlayerWrapperProps extends PropsFromRedux {
  media: any;
  currentTab?: string;
  showDetails?: boolean;
  showUsedBy?: boolean;
  hosts?: any;
}

interface SocialItem {
  id: string;
  job_type: string;
  status: number;
  title?: string;
  mt?: number;
  msg?: string;
  channel_name?: string;
  published_id?: string;
  host?: number;
  schedule?: number;
}

const GeneralPlayerWrapper: React.FC<GeneralPlayerWrapperProps> = ({
  media,
  currentMediaDetailsForEdit,
  currentTab,
  showDetails = true,
  productList,
  agencies,
  showUsedBy = false,
  hosts,
  bunchCategoryList,
  bunchProductList,
  denmarkHosts,
  allHostsList,
}) => {
  const dispatch = useDispatch();
  const [isFullDescriptionVisible, setIsFullDescriptionVisible] =
    useState(false);
  const [isDescriptionClamped, setIsDescriptionClamped] = useState(true);
  const [socials, setSocials] = useState<SocialItem[]>([]);
  const [popoverChip, setPopoverChip] = useState<any>(null);
  const [denmarkEditModalOpen, setDenmarkEditModalOpen] = useState(false);
  const [denmarkEditData, setDenmarkEditData] =
    useState<DenmarkPublishInterface | null>(null);

  const descriptionRef = useRef<HTMLDivElement>(null);

  const handleDenmarkEdit = (publishData: DenmarkPublishInterface) => {
    setDenmarkEditData(publishData);
    setDenmarkEditModalOpen(true);
    setPopoverChip(null);
  };

  const rotatingStyles = `@keyframes rotate {
    0% { transform: rotate(0deg); }
    50% { transform: rotate(180deg); }
    100% { transform: rotate(0deg); }
  }
  .rotating-sandbox {
    animation: rotate 2s infinite linear;
    display: inline-block;
    width: 16px;
    height: 16px;
  }`;

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = rotatingStyles;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const hostOptions = hosts?.map((host: any) => ({
    label: host.name,
    value: host.id,
    logoStyle: getHostLogoStyle(host.name),
  }));

  useEffect(() => {
    const descriptionElement = descriptionRef.current;
    if (descriptionElement) {
      const isClamped =
        descriptionElement.scrollHeight > descriptionElement.clientHeight;
      setIsDescriptionClamped(isClamped);
    }
  }, [media?.desc]);

  const handleRouting = (id: string, job_type: string, channelName: string) => {
    let url = "";

    if (job_type === "youtube") {
      url = `${YOUTUBE_URL}${id}`;
    } else if (job_type === "facebook") {
      url = `${FACEBOOK_URL}${id}`;
    } else if (job_type === "linkedin") {
      url = `${LINKEDIN_URL}${id}`;
    } else if (job_type === "twitter") {
      url = `${TWITTER_URL}${channelName}/status/${id}`;
    } else if (job_type === "instagram") {
      url = `${INSTAGRAM_URL}${id}`;
    }
    if (url) {
      window.open(url, "_blank");
    }
  };

  const handleRoutingPopover = () => {
    if (popoverChip?.chip.status === 10) {
      handleLaunchVideo(
        popoverChip.chip.job_type,
        popoverChip.chip.published_id,
        popoverChip.chip.status,
        popoverChip.chip.channel_name,
        popoverChip.chip.host
      );
    }
  };

  const handleLaunchVideo = async (
    job_type: string,
    published_id?: string | undefined,
    status?: number,
    channelName?: string | undefined,
    hostId?: number
  ) => {
    if (status !== 10) return;
    if ((job_type === "denmark" || job_type === "denmark_embed") && hostId) {
      const isArticle = job_type === "denmark_embed";
      const url = await getDenmarkMediaUrlWithSeopath(
        published_id || "",
        hostId,
        dispatch,
        callDenmarkDetailApi,
        isArticle,
        callDenmarkArticleGetApi,
        allHostsList
      );
      if (url) {
        window.open(url, "_blank");
      }
    } else {
      openVideoInNewTab(job_type, published_id, channelName);
    }
  };

  const getProductNameById = (productId: string) => {
    if (!productId) return "Unknown Product";
    const productItem = bunchProductList?.find((p: any) => p?.id === productId);
    return productItem?.name || productId;
  };

  const getAgencyNameById = (agencyId: string) => {
    if (!agencyId) return "N/A";
    const agencyItem = agencies?.find((a: any) => a.value === agencyId);
    return agencyItem?.name || agencyId;
  };

  const getCategoryNameById = (categoryId: string) => {
    if (!categoryId) return "N/A";
    const categoryItem = bunchCategoryList?.find(
      (c: any) => c?.id === categoryId
    );

    return categoryItem?.name || categoryId;
  };

  const getUniqueTeams = useCallback(
    (childMedia: any[] | undefined) => {
      if (!childMedia || childMedia.length === 0) return [];
      const uniqueTeams = childMedia
        .filter(
          (item: any, index: number, self: any[]) =>
            index === self.findIndex((t: any) => t.team === item.team)
        )
        .map((item: any) => getProductNameById(item.team));
      return uniqueTeams;
    },
    [getProductNameById]
  );

  const uniqueTeamsDisplay = useMemo(() => {
    return getUniqueTeams(media?.child_media);
    return [];
  }, [media?.child_media, getUniqueTeams]);

  const renderStatusIndicator = useCallback(
    (status: number, schedule?: number) => {
      if (schedule && schedule > Date.now()) {
        return <i className="bi bi-clock" style={{ fontSize: "0.8rem" }} />;
      }
      if ([2, 3, 4, 7].includes(status)) {
        return (
          <SandboxIcon
            className="rotating-sandbox"
            width={16}
            height={16}
            style={{ flexShrink: 0 }}
          />
        );
      }

      return (
        <span
          className={`rounded-circle d-inline-block ${
            socialPublishStatusToTextAndColor(status).statusColor
          }`}
          style={{
            height: "10px",
            width: "10px",
          }}
        />
      );
    },
    []
  );

  useEffect(() => {
    if (media?.socials) {
      const currentSocials: SocialItem[] = Array.isArray(media.socials)
        ? media.socials
        : [];
      setSocials(currentSocials);
    }
  }, [media?.socials]);

  return (
    <div className="d-flex gap-3">
      {denmarkEditModalOpen && denmarkEditData && (
        <DenmarkEditModal
          isOpen={denmarkEditModalOpen}
          onClose={() => {
            setDenmarkEditModalOpen(false);
            setDenmarkEditData(null);
          }}
          publishData={denmarkEditData}
          hosts={hosts}
          hostOptions={hostOptions}
          denmarkHosts={denmarkHosts}
        />
      )}
      <div className="flex-grow-1">
        <GeneralPlayer
          id={media?.id}
          autoPlay={2}
          remoteConfig={currentTab === DRIVE_TAB ? true : false}
          playerType={currentTab === DRIVE_TAB ? "native" : ""}
          videoUrl={currentTab === DRIVE_TAB ? media?.previewfile : undefined}
        />
      </div>

      {showDetails && (
        <div
          className="d-flex flex-column gap-3"
          style={{
            width: "35%",
            height: "340px",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {media?.content_type === 2 && (
            <div>
              <label className="text-uppercase">Title:</label>
              <div className="text-primary fw-medium">{media?.title}</div>
            </div>
          )}
          {media?.content_type !== 2 && (
            <div className="d-flex gap-2 H">
              {media?.status !== 5 ? (
                <div
                  className={`${
                    fileProcessingStatusToTextAndColor(media?.status)
                      .statusColor
                  } badge fw-normal d-flex`}
                >
                  {fileProcessingStatusToTextAndColor(media?.status).statusText}
                </div>
              ) : (
                <Button
                  type="outline-secondary"
                  className="pointer"
                  style={{
                    textTransform: "none",
                  }}
                  onClick={async () => {
                    const result = await copyTextToClipboard(media?.id);
                    toast.success("Slike ID copied");
                  }}
                  tooltipText="Copy ID"
                >
                  {media?.id}
                </Button>
              )}
            </div>
          )}

          {media?.desc && (
            <div className="">
              <label className="text-uppercase">Description:</label>
              <div
                ref={descriptionRef}
                className={`text-primary fw-medium lh-base ${
                  isFullDescriptionVisible ? "" : "clamp-lines clamp-2"
                } ${isDescriptionClamped ? "" : "pb-2"}`}
              >
                {media?.desc}
              </div>
              {isDescriptionClamped && (
                <button
                  style={{
                    background: "none",
                    textDecoration: "underline",
                    display: "inline-block",
                  }}
                  className="border-0 p-0 active"
                  onClick={() =>
                    setIsFullDescriptionVisible(!isFullDescriptionVisible)
                  }
                >
                  {isFullDescriptionVisible ? "Read Less" : "Read More"}
                </button>
              )}
            </div>
          )}
          {media?.content_type === 2 &&
            Array.isArray(media?.tags) &&
            media.tags.length > 0 && (
              <div className="">
                <label className="text-uppercase">Tags:</label>
                <div className="d-flex flex-wrap gap-1 mt-1">
                  {media.tags.map((tag: string, index: number) => (
                    <span
                      key={index}
                      className="badge bg-light text-dark border fw-normal"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          {media?.content_type !== 2 && (
            <div className="row gy-3">
              <div className="col-4 H">
                <label className="text-uppercase">Modified:</label>
                <div className="text-primary fw-medium">{`${formatTimestampToAgo(
                  media?.ct
                )} | ${media?.ctbydata?.name}`}</div>
              </div>
              <div className="col-4">
                <label className="text-uppercase">Content Type:</label>
                <div className="text-primary fw-medium">
                  {media?.ctype || "NA"}
                </div>
              </div>
              <div className="col-4">
                <label className="text-uppercase">Category:</label>
                <div className="text-primary fw-medium">
                  {getCategoryNameById(media?.category)}
                </div>
              </div>
              <div className="col-4">
                <label className="text-uppercase">Team:</label>
                <div className="text-primary fw-medium">
                  {formatEntityName(media?.teamdata)}
                </div>
              </div>
              <div className="col-4">
                <label className="text-uppercase">Agency:</label>
                <div className="text-primary fw-medium">
                  {formatEntityName(media?.agencydata) || media?.agency_name}
                </div>
              </div>
              <div className="col-4">
                <label className="text-uppercase">Platform Views:</label>
                <div className="text-primary fw-medium">
                  {media.views?.views
                    ? new Intl.NumberFormat("en-IN").format(
                        Number(media.views.views)
                      )
                    : "0"}
                </div>
              </div>
              <div className="col-4">
                <label className="text-uppercase">Youtube Views:</label>
                <div className="text-primary fw-medium">
                  {media.youtubedata?.view_count
                    ? new Intl.NumberFormat("en-IN").format(
                        Number(media.youtubedata.view_count)
                      )
                    : "0"}
                </div>
              </div>
              {media?.media_ref?.rel && (
                <div className="col-4">
                  <label className="text-uppercase">Source Type:</label>
                  <div className="text-primary fw-medium">
                    {media?.media_ref?.rel}
                  </div>
                </div>
              )}
            </div>
          )}
          {(socials?.length > 0 || uniqueTeamsDisplay.length > 0) && (
            <div className="mt-1 pt-3 border-top">
              <div className="fw-medium text-uppercase">
                Published Destinations
              </div>
              <div className="d-flex flex-wrap mt-2 gap-2">
                {socials?.map((social: SocialItem) => (
                  <div
                    className="social-chip"
                    key={`${social?.id}-${social?.status}`}
                    style={{ maxWidth: "120px" }}
                  >
                    <PublishedIcon
                      job_type={social?.job_type}
                      className="fs-5"
                      status={social?.status}
                    />
                    <span
                      className="text-truncate pointer"
                      onClick={(e: any) => {
                        e.stopPropagation();
                        // if (social?.status === 10) {
                        //   if (social?.job_type === 'denmark') {
                        //     const fullUrl =
                        //       social?.host && social?.published_id
                        //         ? getDenmarkMediaUrl(social.host, social.published_id)
                        //         : '';
                        //     if (fullUrl) {
                        //       window.open(fullUrl, '_blank');
                        //     }
                        //   } else {
                        //     handleRouting(
                        //       social?.job_type === 'linkedin' ? social?.published_id ?? '' : social?.published_id ?? '',
                        //       social?.job_type ?? '',
                        //       social?.channel_name ?? '',
                        //     );
                        //   }
                        // }
                        const rect = (
                          e.currentTarget as HTMLElement
                        ).getBoundingClientRect();
                        if (
                          popoverChip?.chip.channel_name === social.channel_name
                        ) {
                          setPopoverChip(null);
                        } else {
                          setPopoverChip({
                            chip: { ...social },
                            anchor: rect,
                          });
                        }
                      }}
                    >
                      {social?.channel_name}
                    </span>
                    <Tooltip
                      content={
                        social?.schedule && social.schedule > Date.now()
                          ? `Scheduled On: ${new Date(
                              social.schedule
                            ).toLocaleString()}`
                          : social?.status === 10
                            ? `Published On : ${formatTimestampToAgo(
                                social?.mt ?? 0
                              )}`
                            : social?.status === -2 || social?.status === -1
                              ? (social?.msg ??
                                "Your content has failed to publish.")
                              : social?.status === 0
                                ? "Your content is in queue, uploading will begin once content is activated. "
                                : "Your content is getting uploaded, currently in progress."
                      }
                    >
                      {renderStatusIndicator(
                        social?.status ?? 1,
                        social?.schedule
                      )}
                    </Tooltip>
                    <span
                      className="H"
                      onClick={async () => {
                        if (social?.status === 10) {
                          if (
                            social?.job_type === "denmark" ||
                            social?.job_type === "denmark_embed"
                          ) {
                            if (social?.host && social?.published_id) {
                              const isArticle =
                                social?.job_type === "denmark_embed";
                              const fullUrl =
                                await getDenmarkMediaUrlWithSeopath(
                                  social.published_id,
                                  social.host,
                                  dispatch,
                                  callDenmarkDetailApi,
                                  isArticle,
                                  callDenmarkArticleGetApi,
                                  allHostsList
                                );
                              if (fullUrl) {
                                window.open(fullUrl, "_blank");
                              } else {
                                toast.fail("Live URL could not be fetched");
                              }
                            }
                          } else {
                            handleRouting(
                              social?.job_type === "linkedin"
                                ? (social?.published_id ?? "")
                                : (social?.published_id ?? ""),
                              social?.job_type ?? "",
                              social?.channel_name ?? ""
                            );
                          }
                        }
                      }}
                    >
                      <CustomUpload
                        style={{
                          width: "16px",
                          cursor: social?.status !== 10 ? "default" : "pointer",
                        }}
                        className={`${
                          social?.status !== 10 ? "opacity-50" : ""
                        }`}
                      />
                    </span>
                  </div>
                ))}
                {showUsedBy &&
                  uniqueTeamsDisplay.map((teamName, index) => (
                    <span key={`team-${index}`} className="social-chip">
                      <i
                        className="bi bi-people-fill text-primary me-1"
                        style={{ fontSize: "0.8rem" }}
                      ></i>
                      <span
                        className="text-truncate"
                        style={{ maxWidth: "120px" }}
                      >
                        {teamName}
                      </span>
                    </span>
                  ))}
                {popoverChip &&
                  createPortal(
                    <DenmarkPublishPopover
                      chip={{ ...popoverChip.chip }}
                      anchor={popoverChip.anchor}
                      onClose={() => setPopoverChip(null)}
                      handleRouting={handleRoutingPopover}
                      hostLogoStyle={getHostLogoStyleByHostId(
                        popoverChip.chip.host,
                        hosts,
                        hostOptions,
                        denmarkHosts
                      )}
                      publishData={{ ...popoverChip.chip }}
                      currentMediaDetailsForEdit={media}
                      hosts={hosts}
                      hostOptions={hostOptions}
                      denmarkHosts={denmarkHosts}
                      onEdit={handleDenmarkEdit}
                      zIndex={1060}
                    />,
                    document.body
                  )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const mapState = (state: RootState) => ({
  currentMediaDetailsForEdit: state.media.mediaDetails.media,
  productList: state.constants.productList.list,
  agencies: state.constants.agenciesList.list,
  bunchProductList: state.constants.bunchProductList.list,
  bunchCategoryList: state.constants.bunchCategoryList.list,
  allHostsList: state.constants.allHostsList.list,
  denmarkHosts:
    state.constants.allHostsList.list &&
    Array.isArray((state.constants.allHostsList.list as any)?.hosts)
      ? ((state.constants.allHostsList.list as any)?.hosts as any[])
      : Array.isArray(state.constants.allHostsList.list)
        ? (state.constants.allHostsList.list as any[])
        : [],
});

const mapDispatch = {
  callDenmarkDetailApi,
  callDenmarkArticleGetApi,
};

const connector = connect(mapState);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(GeneralPlayerWrapper);
